import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { generatePath, useLocation, useNavigate, useParams } from "react-router-dom";
import { ChevronDown, ExternalLink, FileText, MessageSquare, UserRound } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { toaster } from "@/components/Toaster";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { formatDate, formFileDownloadByPathUrl, formFileDownloadMediaUrl, handleMutationError } from "@/lib/utils";
import { getExaminerAssignment, getExaminerFeedback, postExaminerFeedback } from "./examiner.api";
import type { ExaminerAssignmentResponse, ExaminerFeedbackItem, ExaminerFeedbackResponse } from "./examiner.schema";
import { routes } from "@/app/routes/paths";

type CommentsFormValues = {
	message: string;
	attachment?: File[];
};

const formatScore = (value?: string | null) => {
	const numericValue = Number(value ?? 0);
	return Number.isFinite(numericValue) ? numericValue.toFixed(0) : "0";
};

export const ExaminerFeedback: React.FC = () => {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const location = useLocation();
	const { examinerId, dissertationId } = useParams<{ examinerId: string; dissertationId: string }>();
	const [filesExpanded, setFilesExpanded] = useState(false);
	const [commentsExpanded, setCommentsExpanded] = useState(false);
	const [filesPage, setFilesPage] = useState(1);
	const [commentsPage, setCommentsPage] = useState(1);
	const commentsObserverTarget = useRef<HTMLDivElement>(null);
	const FILES_PER_PAGE = 6;
	const COMMENTS_PER_PAGE = 10;

	const assignmentQuery = useQuery<ExaminerAssignmentResponse>({
		queryKey: ["examiner-assignment", dissertationId, examinerId],
		queryFn: async () => {
			if (!examinerId || !dissertationId) {
				throw new Error("Missing examiner or dissertation information in the page URL.");
			}

			return getExaminerAssignment(dissertationId, examinerId);
		},
		enabled: Boolean(examinerId && dissertationId),
		retry: false,
		refetchOnWindowFocus: false,
	});

	const assignment = assignmentQuery.data?.data;
	const dissertation = assignment?.dissertation ?? null;
	const examiner = assignment?.examiner ?? null;
	const scoreSheet = assignment?.score_sheet ?? null;

	const { data: filesResp, isLoading: isFilesLoading } = useQuery({
		queryKey: ["examiner-dissertation-files", assignment?.assignment?.key],
		queryFn: async () => {
			const res = await api.get(`/review-center/${dissertation?.key}/${assignment?.assignment?.key}/files`);
			return res.data;
		},
		enabled: Boolean(assignment?.assignment?.key),
	});

	const { data: commentsResp, isLoading: isCommentsLoading } = useQuery<ExaminerFeedbackResponse>({
		queryKey: ["examiner-dissertation-feedback", dissertation?.key, examiner?.key],
		queryFn: async () => {
			if (!dissertation?.key || !examiner?.key) {
				throw new Error("Missing examiner or dissertation key.");
			}

			return getExaminerFeedback(dissertation.key, examiner.key);
		},
		enabled: Boolean(dissertation?.key && examiner?.key),
	});

	const files: { name: string; path: string }[] = Object.values(filesResp?.data ?? {});
	const comments: ExaminerFeedbackItem[] = commentsResp?.data ?? [];
	const paginatedFiles = files.slice(0, filesPage * FILES_PER_PAGE);
	const hasMoreFiles = paginatedFiles.length < files.length;
	const paginatedComments = comments.slice(0, commentsPage * COMMENTS_PER_PAGE);
	const hasMoreComments = paginatedComments.length < comments.length;

	const scoreRows = !assignment?.sections?.length || !scoreSheet
		? []
		: assignment.sections.map((section) => {
			const scoreValue = scoreSheet[section.field];

			return {
				...section,
				score: Number(scoreValue ?? 0),
			};
		});

	useEffect(() => {
		if (dissertation?.title) {
			document.title = `${dissertation.title} - Examiner Feedback`;
		}
	}, [dissertation?.title]);

	useEffect(() => {
		if (assignment && !assignment.score_sheet_submitted) {
			navigate(generatePath(routes.EXAMINER_SCORESHEET_PATH, { examinerId, dissertationId }));
		}
	}, [assignment, location.pathname, navigate, examinerId, dissertationId]);

	useEffect(() => {
		const observer = new IntersectionObserver(([entry]) => {
			if (entry.isIntersecting && hasMoreComments && commentsExpanded) {
				setCommentsPage((page) => page + 1);
			}
		}, { threshold: 0.1 });

		if (commentsObserverTarget.current) {
			observer.observe(commentsObserverTarget.current);
		}

		return () => observer.disconnect();
	}, [commentsExpanded, hasMoreComments]);

	const form = useForm<CommentsFormValues>({
		defaultValues: {
			message: "",
			attachment: undefined,
		},
	});

	const commentMutation = useMutation({
		mutationFn: (data: FormData) => {
			if (!dissertation?.key || !examiner?.key) {
				throw new Error("Missing examiner or dissertation key.");
			}

			return postExaminerFeedback(dissertation.key, examiner.key, data);
		},
		onSuccess: () => {
			toaster("success", "Comment posted");
			queryClient.invalidateQueries({ queryKey: ["examiner-dissertation-feedback", dissertation?.key, examiner?.key] });
			setCommentsPage(1);
			form.reset({
				message: "",
				attachment: undefined,
			});
		},
		onError: handleMutationError,
	});

	const onSubmit = (values: CommentsFormValues) => {
		const formData = new FormData();
		formData.append("comment", values.message || "");

		values.attachment?.forEach((file) => {
			formData.append("file_attached[]", file);
		});

		commentMutation.mutate(formData);
	};

	if (assignmentQuery.isLoading) {
		return (
			<div className="min-h-screen bg-stone-50 px-4 py-12 text-stone-800">
				<div className="mx-auto max-w-6xl rounded-3xl border border-stone-200 bg-white p-8 shadow-sm">
					Loading feedback page...
				</div>
			</div>
		);
	}

	if (assignmentQuery.isError || !assignment || !dissertation) {
		return (
			<div className="min-h-screen bg-stone-50 px-4 py-12 text-stone-800">
				<div className="mx-auto max-w-4xl rounded-3xl border border-rose-200 bg-white p-8 shadow-sm">
					<h1 className="text-2xl font-semibold text-stone-900">Feedback Page Unavailable</h1>
					<p className="mt-3 text-sm leading-6 text-stone-600">
						We could not load the examiner assignment details for this dissertation. Please confirm the link is correct and try again later.
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-[radial-gradient(circle_at_top_left,#f5f5f4,#fafaf9_40%,#e7e5e4_100%)] px-4 py-8 text-stone-900">
			<div className="mx-auto max-w-7xl space-y-6">
				<div className="overflow-hidden rounded-[2rem] border border-stone-200 bg-white shadow-[0_20px_80px_rgba(28,25,23,0.08)]">
					<div className="border-b border-stone-200 bg-[linear-gradient(135deg,#672e3b,#44403c)] px-8 py-8 text-white">
						<div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
							<div className="space-y-3">
								<p className="text-xs font-semibold uppercase tracking-[0.3em] text-stone-300">
									Examiner Review Workspace
								</p>
								<h1 className="max-w-4xl text-3xl font-semibold leading-tight">
									{dissertation.title}
								</h1>
								<p className="max-w-2xl text-sm leading-6 text-stone-300">
									Review materials, track your submitted score sheet, exchange comments with the candidate, and download dissertation files from one place.
								</p>
							</div>

							<div className="grid gap-3 sm:grid-cols-2">
								<div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
									<div className="text-xs uppercase tracking-wide text-stone-300">Resident</div>
									<div className="mt-1 text-lg font-semibold">{dissertation.candidate?.name || "—"}</div>
								</div>
								<div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
									<div className="text-xs uppercase tracking-wide text-stone-300">Total Score</div>
									<div className="mt-1 text-lg font-semibold">{formatScore(scoreSheet?.total_score)} / 100</div>
								</div>
							</div>
						</div>
					</div>

					<div className="grid gap-6 px-8 py-8 xl:grid-cols-[1.5fr_0.9fr]">
						<div className="space-y-6">
							
							<section className="overflow-hidden rounded-3xl border border-stone-200 bg-white">
								<button
									type="button"
									onClick={() => setFilesExpanded((value) => !value)}
									className="flex w-full items-center justify-between bg-stone-50 px-6 py-5 text-left transition-colors hover:bg-stone-100"
								>
									<div className="flex items-center gap-3">
										<div className="rounded-2xl bg-white p-3 shadow-sm">
											<FileText className="h-5 w-5 text-stone-700" />
										</div>
										<div>
											<h2 className="font-semibold">Resident Files</h2>
											<p className="text-sm text-stone-600">{files.length} file(s) available</p>
										</div>
									</div>
									<ChevronDown className={`h-5 w-5 transition-transform ${filesExpanded ? "rotate-0" : "-rotate-90"}`} />
								</button>

								{filesExpanded && (
									<div className="space-y-3 p-6">
										{isFilesLoading ? (
											<div className="text-sm text-stone-600">Loading files...</div>
										) : paginatedFiles.length ? (
											<>
												{paginatedFiles.map((file) => (
													<div key={file.name} className="flex flex-col gap-3 rounded-2xl border border-stone-200 bg-stone-50 p-4 sm:flex-row sm:items-center sm:justify-between">
														<div className="min-w-0">
															<div className="truncate font-medium text-stone-900">{file.name.replaceAll("_", " ").charAt(0).toUpperCase() + file.name.replaceAll("_", " ").slice(1)}</div>
															<div className="text-sm text-stone-500">Candidate submission file</div>
														</div>
														<div className="flex gap-2">
															<a href={formFileDownloadByPathUrl(`dissertations/${dissertation.key}/${file.name}`)}>
																<Button variant="outline" size="sm">
																	<ExternalLink className="mr-2 h-4 w-4" />
																	View
																</Button>
															</a>
														</div>
													</div>
												))}

												{hasMoreFiles && (
													<div className="pt-2 text-center">
														<Button variant="outline" onClick={() => setFilesPage((page) => page + 1)} size="sm">
															Load More Files ({files.length - paginatedFiles.length} remaining)
														</Button>
													</div>
												)}
											</>
										) : (
											<div className="text-sm text-stone-600">No files found for this dissertation.</div>
										)}
									</div>
								)}
							</section>
							<section className="rounded-3xl border border-stone-200 bg-stone-50 p-6">
								<div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
									<div>
										<h2 className="text-xl font-semibold text-stone-900">Dissertation Overview</h2>
										<p className="mt-2 text-sm leading-6 text-stone-600">
											A structured summary of the candidate submission and current review state.
										</p>
									</div>
									<div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
										<div className="font-medium">{dissertation.status_name}</div>
										<div className="text-xs text-amber-700">Assignment status: {assignment.assignment.status_name}</div>
									</div>
								</div>

								<div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
									<div className="rounded-2xl border border-stone-200 bg-white p-4">
										<div className="text-xs uppercase tracking-wide text-stone-500">Faculty</div>
										<div className="mt-2 font-medium">{dissertation.faculty?.name || "—"}</div>
									</div>
									<div className="rounded-2xl border border-stone-200 bg-white p-4">
										<div className="text-xs uppercase tracking-wide text-stone-500">Examiner Type</div>
										<div className="mt-2 font-medium">{assignment.assignment.type_name}</div>
									</div>
									<div className="rounded-2xl border border-stone-200 bg-white p-4">
										<div className="text-xs uppercase tracking-wide text-stone-500">Deadline</div>
										<div className="mt-2 font-medium">{formatDate(assignment.assignment.deadline, "Do MMM, YYYY") || "—"}</div>
									</div>
									<div className="rounded-2xl border border-stone-200 bg-white p-4">
										<div className="text-xs uppercase tracking-wide text-stone-500">Submitted At</div>
										<div className="mt-2 font-medium">{formatDate(scoreSheet?.submitted_at, "Do MMM, YYYY - H:mm a") || "—"}</div>
									</div>
								</div>

							</section>
							<section className="overflow-hidden rounded-3xl border border-stone-200 bg-white">
								<button
									type="button"
									onClick={() => setCommentsExpanded((value) => !value)}
									className="flex w-full items-center justify-between bg-stone-50 px-6 py-5 text-left transition-colors hover:bg-stone-100"
								>
									<div className="flex items-center gap-3">
										<div className="rounded-2xl bg-white p-3 shadow-sm">
											<MessageSquare className="h-5 w-5 text-stone-700" />
										</div>
										<div>
											<h2 className="font-semibold">Comments & File Exchange</h2>
											<p className="text-sm text-stone-600">{comments.length} discussion item(s)</p>
										</div>
									</div>
									<ChevronDown className={`h-5 w-5 transition-transform ${commentsExpanded ? "rotate-0" : "-rotate-90"}`} />
								</button>

								{commentsExpanded && (
									<div className="space-y-5 p-6">
										<div className="rounded-2xl border border-stone-200 bg-stone-50 p-4">
											<h3 className="text-base font-semibold">Post a Comment</h3>
											<p className="mt-1 text-sm text-stone-600">
												Use this thread to share clarifications, requests, and supporting files with the candidate.
											</p>

											<Form {...form}>
												<form onSubmit={form.handleSubmit(onSubmit)} className="mt-4 space-y-4">
													<FormField
														control={form.control}
														name="message"
														render={({ field }) => (
															<FormItem>
																<FormControl>
																	<RichTextEditor value={field.value} onChange={field.onChange} />
																</FormControl>
																<FormMessage />
															</FormItem>
														)}
													/>

													<FormField
														control={form.control}
														name="attachment"
														render={({ field: { onChange } }) => (
															<FormItem>
																<FormControl>
																	<Input
																		type="file"
																		multiple
																		onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(Array.from(event.target.files ?? []))}
																	/>
																</FormControl>
																<FormMessage />
															</FormItem>
														)}
													/>

													<div className="flex justify-end">
														<Button type="submit" loading={commentMutation.isPending} disabled={commentMutation.isPending}>
															Post Comment
														</Button>
													</div>
												</form>
											</Form>
										</div>
										<div className="space-y-3">
											{isCommentsLoading ? (
												<div className="text-sm text-stone-600">Loading comments...</div>
											) : paginatedComments.length ? (
												<>
													{paginatedComments.map((comment) => (
														<div key={comment.id} className="rounded-2xl border border-stone-200 bg-stone-50 p-4">
															<div className="flex flex-wrap items-center justify-between gap-2">
																<div className="font-medium text-stone-900">{comment.user?.name || dissertation.candidate?.name || "Unknown Sender"}</div>
																<div className="text-xs uppercase tracking-wide text-stone-500">{formatDate(comment.created_at, "Do MMM, YYYY - h:mm a")}</div>
															</div>
															<div
																className="prose prose-stone mt-3 max-w-none text-sm"
																dangerouslySetInnerHTML={{ __html: comment.comment }}
															/>
															{comment.attachments && comment.attachments.length > 0 ? (
																<div className="mt-4 space-y-2">
																	<div className="text-xs font-semibold uppercase tracking-wide text-stone-500">Attachments</div>
																	{comment.attachments.map((attachment) => (
																		<div key={attachment.id} className="flex items-center justify-between rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm">
																			<div className="truncate pr-3">{attachment.original_file_name || "Attached file"}</div>
																			<a href={formFileDownloadMediaUrl(attachment.file_url)} target="_blank" rel="noreferrer" className="text-stone-700 underline underline-offset-4">
																				View
																			</a>
																		</div>
																	))}
																</div>
															) : comment.has_file && comment.file_url ? (
																<div className="mt-4 space-y-2">
																	<div className="text-xs font-semibold uppercase tracking-wide text-stone-500">Attachments</div>
																	<div className="flex items-center justify-between rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm">
																		<div className="truncate pr-3">{comment.original_file_name || "Attached file"}</div>
																		<a href={formFileDownloadMediaUrl(comment.file_url)} target="_blank" rel="noreferrer" className="text-stone-700 underline underline-offset-4">
																			View
																		</a>
																	</div>
																</div>
															) : null}
														</div>
													))}

													<div ref={commentsObserverTarget} className="h-3" />
													{hasMoreComments && (
														<div className="pt-2 text-center">
															<Button variant="outline" onClick={() => setCommentsPage((page) => page + 1)} size="sm">
																Load More Comments ({comments.length - paginatedComments.length} remaining)
															</Button>
														</div>
													)}
												</>
											) : (
												<div className="rounded-2xl border border-dashed border-stone-300 bg-stone-50 p-4 text-sm text-stone-600">
													No comments yet. Start the discussion below.
												</div>
											)}
										</div>
									</div>
								)}
							</section>
						</div>

						<div className="space-y-6">
							<section className="rounded-3xl border border-stone-200 bg-white p-6">
								<div className="flex items-center justify-between gap-4">
									<div>
										<h2 className="text-xl font-semibold">Submitted Score Sheet</h2>
										<p className="mt-2 text-sm text-stone-600">
											Your scoring breakdown and final comments as recorded for this dissertation.
										</p>
									</div>
									<div className="rounded-full bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700">
										{scoreSheet?.status_name || "Submitted"}
									</div>
								</div>

								<div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-2">
									{scoreRows.map((row) => (
										<div key={row.key} className="rounded-2xl border border-stone-200 bg-stone-50 p-4">
											<div className="text-xs font-semibold uppercase tracking-wide text-stone-500">
												{row.section}. {row.label}
											</div>
											<div className="mt-3 text-2xl font-semibold text-stone-900">
												{row.score.toFixed(0)} <span className="text-sm font-medium text-stone-500">/ {row.max_score}</span>
											</div>
										</div>
									))}
								</div>

								<div className="mt-6 grid gap-6">
									<div className="rounded-2xl border border-stone-200 bg-stone-50 p-5">
										<div className="text-xs font-semibold uppercase tracking-wide text-stone-500">General Comments</div>
										<p className="mt-3 whitespace-pre-wrap text-sm leading-7 text-stone-700">
											{scoreSheet?.general_comments || "No general comments were submitted with this score sheet."}
										</p>
									</div>
								</div>
							</section>
							<section className="rounded-3xl border border-stone-200 bg-white p-6">
								<div className="flex items-center gap-3">
									<div className="rounded-2xl bg-stone-100 p-3">
										<UserRound className="h-5 w-5 text-stone-700" />
									</div>
									<div>
										<h2 className="text-lg font-semibold">Examiner Details</h2>
										<p className="text-sm text-stone-600">Reference details tied to this review link.</p>
									</div>
								</div>

								<div className="mt-5 space-y-4">
									<div className="rounded-2xl border border-stone-200 bg-stone-50 p-4">
										<div className="text-xs uppercase tracking-wide text-stone-500">Name</div>
										<div className="mt-1 font-medium">{`${examiner?.full_name}` || "—"}</div>
									</div>
									<div className="rounded-2xl border border-stone-200 bg-stone-50 p-4">
										<div className="text-xs uppercase tracking-wide text-stone-500">Email</div>
										<div className="mt-1 break-all font-medium">{examiner?.email || "—"}</div>
									</div>
									<div className="rounded-2xl border border-stone-200 bg-stone-50 p-4">
										<div className="text-xs uppercase tracking-wide text-stone-500">Mobile</div>
										<div className="mt-1 font-medium">{examiner?.mobile || "—"}</div>
									</div>
									<div className="rounded-2xl border border-stone-200 bg-stone-50 p-4">
										<div className="text-xs uppercase tracking-wide text-stone-500">Institution</div>
										<div className="mt-1 font-medium">{examiner?.institution || "—"}</div>
									</div>
								</div>
							</section>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ExaminerFeedback;
